import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Text,
  VStack,
  useColorMode,
  useColorModeValue,
  useToast,
  Image,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../stores/authStore";
import { PublicNavbar } from "./layout/PublicNavbar";
import { Footer } from "./layout/Footer";
import Turnstile from "react-turnstile";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3013";
const isDevelopment = process.env.REACT_APP_ENV === "development";

export const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const { login, token } = useAuthStore();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const inputBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Check for existing session
  useEffect(() => {
    if (token) {
      navigate("/chat");
    }
  }, [token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isDevelopment && !turnstileToken) {
      toast({
        title: t("auth.login.error"),
        description: t("auth.login.pleaseVerify"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      await login(email, password, turnstileToken || "dev-mode");
      toast({
        title: t("auth.login.success"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/dashboard");
    } catch (error) {
      toast({
        title: t("auth.login.error"),
        description: t("auth.login.errorMessage"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PublicNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.sm" mt={20}>
          <VStack spacing={8}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("auth.login.title")}
              </Heading>
            </VStack>

            <Box
              as="form"
              onSubmit={handleSubmit}
              bg={inputBg}
              p={8}
              borderRadius="xl"
              border="1px"
              borderColor={borderColor}
              shadow="md"
              w="full"
            >
              <VStack spacing={4}>
                <FormControl id="email" isRequired>
                  <FormLabel>{t("auth.login.email")}</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    bg={inputBg}
                  />
                </FormControl>

                <FormControl id="password" isRequired>
                  <FormLabel>{t("auth.login.password")}</FormLabel>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    bg={inputBg}
                  />
                </FormControl>

                {!isDevelopment && (
                  <Box w="full" display="flex" justifyContent="center" my={4}>
                    <Turnstile
                      sitekey={process.env.REACT_APP_CLOUDFLARE_TURNSTILE || ""}
                      onVerify={setTurnstileToken}
                      theme={colorMode}
                    />
                  </Box>
                )}

                <Button
                  type="submit"
                  colorScheme="brand"
                  size="lg"
                  w="full"
                  isLoading={isLoading}
                >
                  {t("auth.login.submit")}
                </Button>

                <Button
                  w="full"
                  size="lg"
                  variant="outline"
                  leftIcon={
                    <Image
                      src="/assets/img/google.svg"
                      alt="Google"
                      boxSize="20px"
                    />
                  }
                  onClick={() => {
                    const referralCode = sessionStorage.getItem("referralCode");
                    const googleAuthUrl = `${API_URL}/auth/google${
                      referralCode ? `?referralCode=${referralCode}` : ""
                    }`;
                    window.location.href = googleAuthUrl;
                  }}
                >
                  {t("auth.login.googleButton")}
                </Button>

                <Button
                  w="full"
                  size="lg"
                  variant="outline"
                  leftIcon={
                    <Image
                      src="/assets/img/github.svg"
                      alt="GitHub"
                      boxSize="20px"
                    />
                  }
                  onClick={() => {
                    const referralCode = sessionStorage.getItem("referralCode");
                    const githubAuthUrl = `${API_URL}/auth/github${
                      referralCode ? `?referralCode=${referralCode}` : ""
                    }`;
                    window.location.href = githubAuthUrl;
                  }}
                >
                  {t("auth.login.githubButton")}
                </Button>

                <Button
                  w="full"
                  size="lg"
                  variant="outline"
                  leftIcon={
                    <Image
                      src="/assets/img/twitter.svg"
                      alt="Twitter"
                      boxSize="20px"
                    />
                  }
                  onClick={() => {
                    const referralCode = sessionStorage.getItem("referralCode");
                    const twitterAuthUrl = `${API_URL}/auth/twitter${
                      referralCode ? `?referralCode=${referralCode}` : ""
                    }`;
                    window.location.href = twitterAuthUrl;
                  }}
                >
                  {t("auth.login.twitterButton")}
                </Button>

                <Button
                  w="full"
                  size="lg"
                  variant="outline"
                  leftIcon={
                    <Image
                      src="/assets/img/facebook.svg"
                      alt="Facebook"
                      boxSize="20px"
                    />
                  }
                  onClick={() => {
                    const referralCode = sessionStorage.getItem("referralCode");
                    const facebookAuthUrl = `${API_URL}/auth/facebook${
                      referralCode ? `?referralCode=${referralCode}` : ""
                    }`;
                    // console.log("facebookAuthUrl", { facebookAuthUrl });
                    window.location.href = facebookAuthUrl;
                  }}
                >
                  {t("auth.login.facebookButton")}
                </Button>

                <Text>
                  {t("auth.login.noAccount")}{" "}
                  <Link
                    as={RouterLink}
                    to="/register"
                    color="brand.primary"
                    _hover={{
                      textDecoration: "none",
                      color: "brand.secondary",
                    }}
                  >
                    {t("auth.login.registerLink")}
                  </Link>
                </Text>
                <Link
                  as={RouterLink}
                  to="/forgot-password"
                  color="brand.primary"
                >
                  {t("auth.forgotPassword")}
                </Link>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
